// DictionaryResolver.js
// Singleton resolver for phrase dictionaries (phrases ONLY; no select options).
// NEW: Uses AssetsRegistry as backend instead of a getAssetByKey callback + raw S3.
//
// Source of truth: assets stored in S3, fetched via AssetsRegistry.getAssetJson.
// Cache key = `${token}:${lang}`, validated via assetEntry.contentHash to prevent staleness.
//
// DEFAULT ASSET KEY FORMAT (hard-coded style):
//   <TOKEN>_dictionary_<LANG>
// Examples:
//   SFNDEA_dictionary_EN
//   SFNDEA_dictionary_ES
//
// Notes:
// - TOKEN is the current form/project token provided via init({ token }) or init({ currentFormToken }).
// - LANG is derived from the requested language, uppercased, and normalized to its base (e.g. "en-GB" -> "EN").
// - You may still override buildAssetKey in init if needed.

import {
  getAssetEntryByKey,
  getAssetJson,
} from '../asset/AssetsRegistry';

/* ------------------------------ Validation ------------------------------- */

function validatePhrasesObject(obj, where = 'phrases') {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    throw new Error(`[${where}] must be a plain object of key -> string`);
  }
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v !== 'string') {
      throw new Error(`[${where}] key '${k}' must be a string`);
    }
  }
  return obj;
}

/* ------------------------------ Language utils --------------------------- */

function toAssetLangSuffix(lang) {
  // Normalize to base language and uppercase, e.g., "en", "en-GB" -> "EN"
  if (!lang) return '';
  const base = String(lang).split('-')[0];
  return base ? base.toUpperCase() : '';
}

/* --------------------------------- State --------------------------------- */

const state = {
  // Provided at init:
  token: null,          // project/form scope (e.g., "SFNDEA")
  formLanguages: null,  // optional: array of languages (e.g., ["en", "es"])

  // Default asset key builder for the hard-coded format:
  // <TOKEN>_dictionary_<LANG>
  // Example: "SFNDEA_dictionary_EN"
  buildAssetKey(lang) {
    const token = state.token;
    if (!token) {
      throw new Error('DictionaryResolver: token is required to build asset key');
    }
    const LANG = toAssetLangSuffix(lang);
    if (!LANG) {
      throw new Error(`DictionaryResolver: invalid language "${lang}"`);
    }
    return `${token}_dictionary_${LANG}`;
  },

  // Cache (per token+lang): { hash, data }
  // hash comes from AssetEntry.contentHash (if present) to avoid staleness.
  byLang: new Map(), // key: `${token}:${lang}` -> { hash: string, data: { [k]: string } }
};

/* --------------------------------- API ----------------------------------- */

const DictionaryResolver = {
  /**
   * Initialize resolver and preload all provided languages.
   * Accepts either { token } or { currentFormToken } as the token source.
   *
   * @param {{
   *   token?: string,
   *   currentFormToken?: string,
   *   buildAssetKey?: Function,
   *   formLanguages?: string[]
   * }} args
   */
  async init({ token, currentFormToken, buildAssetKey, formLanguages } = {}) {
    // Support both 'token' and 'currentFormToken' (must not conflict)
    const suppliedToken = token ?? currentFormToken ?? state.token;
    if (token && currentFormToken && token !== currentFormToken) {
      throw new Error(
        'DictionaryResolver.init: both "token" and "currentFormToken" provided with different values',
      );
    }
    state.token = suppliedToken ?? state.token;

    if (buildAssetKey !== undefined && typeof buildAssetKey === 'function') {
      state.buildAssetKey = buildAssetKey;
    }

    if (formLanguages !== undefined) {
      state.formLanguages = Array.isArray(formLanguages)
        ? formLanguages.slice()
        : null;
    }

    if (!state.token) {
      throw new Error('DictionaryResolver.init: token (or currentFormToken) is required');
    }

    // Preload all provided form languages, if any
    const langs = Array.isArray(state.formLanguages) ? state.formLanguages : [];
    if (langs.length === 0) return;

    const uniqueLangs = [...new Set(langs.map(String))];
    await Promise.all(uniqueLangs.map((lang) => this._ensure(lang)));
  },

  clearCache() {
    state.byLang.clear();
  },

  /**
   * Ensure dictionary for lang is loaded and current (by AssetEntry.contentHash).
   * @param {string} lang
   */
  async prefetch(lang) {
    await this._ensure(lang);
  },

  /**
   * Get a phrase by key (async, ensures the dictionary is loaded/validated).
   * Returns the key itself if missing or not loaded.
   * @param {string} lang
   * @param {string} key
   */
  async getPhrase(lang, key) {
    const dict = await this.getAll(lang);
    if (!dict) return key;
    const v = dict[key];
    return typeof v === 'string' ? v : key;
  },

  /**
   * Synchronous getter from cache (will NOT fetch).
   * Returns the key itself if not cached or missing.
   * @param {string} lang
   * @param {string} key
   */
  getPhraseSync(lang, key) {
    const k = this._cacheKey(lang);
    const cached = state.byLang.get(k);
    if (!cached) return key;
    const v = cached.data?.[key];
    return typeof v === 'string' ? v : key;
  },

  /**
   * Get entire phrases object (async). Validates/upgrades cache by contentHash.
   * @param {string} lang
   * @returns {Promise<object|null>}
   */
  async getAll(lang) {
    const entry = await this._ensure(lang);
    return entry?.data ?? null;
  },

  /**
   * Convenience: returns a function (key) => Promise<string>
   * Ensures the dictionary is loaded/validated.
   */
  getAsyncGetter(lang) {
    return async (key) => this.getPhrase(lang, key);
  },

  /**
   * Convenience: returns a function (key) => string
   * Sync; reads cache only (returns key if missing/not cached).
   */
  getSyncGetter(lang) {
    return (key) => this.getPhraseSync(lang, key);
  },

  /* ----------------------------- Internals ------------------------------ */

  _cacheKey(lang) {
    const token = state.token || '';
    // Keep the original requested lang (not uppercased) to avoid collisions if caller distinguishes
    return `${token}:${lang}`;
  },

  async _ensure(lang) {
    if (!lang) return null;

    const k = this._cacheKey(lang);

    // If formLanguages provided, refuse unknown langs (case-insensitive on base code)
    if (Array.isArray(state.formLanguages) && state.formLanguages.length > 0) {
      const base = String(lang).split('-')[0].toLowerCase();
      const ok = state.formLanguages.some(
        (fl) => String(fl).split('-')[0].toLowerCase() === base,
      );
      if (!ok) {
        state.byLang.delete(k);
        return null;
      }
    }

    // Resolve asset row (by key "<TOKEN>_dictionary_<LANG>")
    const assetKey = state.buildAssetKey(lang);
    const assetEntry = getAssetEntryByKey(assetKey);

    if (!assetEntry) {
      state.byLang.delete(k);
      return null;
    }

    const newHash =
      assetEntry.contentHash || String(assetEntry.updatedAt || '');

    const cached = state.byLang.get(k);
    if (cached && cached.hash && newHash && cached.hash === newHash) {
      return cached;
    }

    // Fetch JSON via AssetsRegistry (handles S3 + internal cache)
    const data = await getAssetJson({ assetKey });
    const phrases = validatePhrasesObject(data, `asset(${assetKey})`);

    const entry = { hash: newHash || '', data: phrases };
    state.byLang.set(k, entry);
    return entry;
  },
};

export default DictionaryResolver;
